import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import LoadingModal from 'src/components/navigation/LoadingModal';
import { CURRENCIES } from 'src/features/payStations/types';
import { useListTotalValidationsStatistics } from 'src/features/statistics/api/useListTotalValidationsStatistics';
import { useListValidationDetailsStatistics } from 'src/features/statistics/api/useListValidationDetailsStatistics';
import {
    VALIDATION_OPERATIONS_EXPORT_MESSAGES_MAP,
    VALIDATION_OPERATIONS_EXPORT_PROPS,
    type StatisticOperationDetails,
} from 'src/features/statistics/types';
import { statisticsMessages, TOTAL_SPACES_NAME } from 'src/features/statistics/utils';
import { Button } from 'src/registry/ui/Button';
import { formatTime } from 'src/utils/formatTime';
import { getFormattedDateTime } from 'src/utils/getFormattedDateTime';
import { generalMessages } from 'src/utils/messages';

type Props = {
    parkingId: string;
    currency: CURRENCIES;
    dateTime: {
        beforeDate: Date;
        afterDate: Date;
    };
    querySortingOptions: { ordering?: string };
    queryFiltersOptions: { [p: string]: any } | any[];
    querySearchOptions: { [p: string]: string };
    disabled: boolean;
};

type ExtraValidationOperationsData = {
    amount: string;
    name: string;
    currency: CURRENCIES;
}[];

const LIMIT = 100;

export default function ValidationOperationsExport({
    parkingId,
    currency,
    dateTime,
    querySortingOptions,
    queryFiltersOptions,
    querySearchOptions,
    disabled,
}: Props) {
    const [offset, setOffset] = React.useState<number | null>(null);
    const [exportJSON, setExportJSON] = React.useState<StatisticOperationDetails[]>([]);
    const { formatMessage } = useIntl();

    const enabled = React.useMemo(() => typeof offset === 'number', [offset]);

    const { data: exportDetails } = useListValidationDetailsStatistics({
        parkingId,
        to_date: dateTime.beforeDate.toISOString(),
        from_date: dateTime.afterDate.toISOString(),
        ...querySortingOptions,
        ...queryFiltersOptions,
        ...querySearchOptions,
        offset: offset || 0,
        limit: LIMIT,
        config: { enabled },
    });

    const { data: totalValidations } = useListTotalValidationsStatistics({
        parkingId,
        to_date: dateTime.beforeDate.toISOString(),
        from_date: dateTime.afterDate.toISOString(),
        ...querySortingOptions,
        ...queryFiltersOptions,
        ...querySearchOptions,
        config: { enabled },
    });

    const cardData = React.useMemo(() => {
        if (!totalValidations) return [];
        const result = totalValidations.results.map((validation) => {
            return {
                name:
                    validation.result_type === TOTAL_SPACES_NAME
                        ? formatMessage(statisticsMessages.allOperations)
                        : validation.operator_name,
                amount: (validation.due_amount / 100).toFixed(2),
                currency,
            };
        });
        const total = result.shift();
        if (total) result.push(total);
        return result;
    }, [totalValidations]);

    const onExportEnd = React.useCallback(() => {
        setExportJSON([]);
        setOffset(null);
    }, []);

    const json2Csv = (jsonData: StatisticOperationDetails[], extraJsonData: ExtraValidationOperationsData) => {
        const keys = Object.values(VALIDATION_OPERATIONS_EXPORT_PROPS) as VALIDATION_OPERATIONS_EXPORT_PROPS[];
        const csvRows = [];

        csvRows.push(keys.map((key) => formatMessage(VALIDATION_OPERATIONS_EXPORT_MESSAGES_MAP[key])).join(','));
        jsonData.forEach((row) => {
            const values = keys
                .filter((key) => key !== VALIDATION_OPERATIONS_EXPORT_PROPS.TIME)
                .map((key) => {
                    const rawValue = (row as Record<string, any>)[key] as string | null | undefined;
                    const value = rawValue === null || rawValue === undefined ? '' : rawValue.toString();
                    if (key === VALIDATION_OPERATIONS_EXPORT_PROPS.EVENT_DATETIME) {
                        const datetime = new Date(value);
                        const formattedDate = new Date(datetime.toString()).toLocaleDateString('bg-BG');
                        const formattedTime = formatTime(datetime);
                        return `${formattedDate},${formattedTime}`;
                    }
                    switch (key) {
                        case VALIDATION_OPERATIONS_EXPORT_PROPS.DUE_AMOUNT:
                            return (Number(value) / 100).toFixed(2);
                    }

                    if (value.includes(',')) return `"${value}"`;

                    return value;
                });
            csvRows.push(values.join(','));
        });

        csvRows.push([].join(','));
        extraJsonData.map((data) => csvRows.push([data.name, data.amount, data.currency].join(',')));

        return csvRows.join('\n');
    };

    const generateCSV = (
        data: StatisticOperationDetails[],
        title: string,
        extraData: ExtraValidationOperationsData,
    ) => {
        const csv = json2Csv(data, extraData);
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const { formattedDate } = getFormattedDateTime(new Date().toISOString());
        link.href = url;
        link.download = `${title}_${formattedDate}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    React.useEffect(() => {
        if (!enabled || !exportDetails || !exportDetails.results || !cardData) return;
        if (exportDetails.count > (offset || 0) + LIMIT) {
            setExportJSON((prevState) => prevState.concat(exportDetails.results));
            return setOffset((prevState) => (prevState || 0) + LIMIT);
        }
        generateCSV(exportJSON.concat(exportDetails.results), 'Validation operations', cardData);
        onExportEnd();
    }, [setExportJSON, offset, generateCSV, exportDetails, cardData]);

    return (
        <>
            <LoadingModal
                isLoading={enabled}
                title={<FormattedMessage {...statisticsMessages.exportingOperations} />}
                onCancel={onExportEnd}
            />
            <Button onClick={() => setOffset(0)} disabled={disabled}>
                <FormattedMessage {...generalMessages.exportCSV} />
            </Button>
        </>
    );
}
